import { supabase } from "@/integrations/supabase/client";
import { errorHandler } from "@/lib/errorHandler";

// Production-ready infrastructure with comprehensive error handling,
// rate limiting, and performance monitoring capabilities

export class ProductionInfrastructure {
  private isInitialized = false;

  async initialize() {
    if (this.isInitialized) return;

    try {
      // Initialize performance monitoring
      this.initializePerformanceObserver();
      
      // Set up error tracking
      this.setupErrorTracking();
      
      // Initialize rate limiting cleanup
      this.scheduleRateLimitCleanup();
      
      this.isInitialized = true;
      console.log('🚀 Production infrastructure initialized');
    } catch (error) {
      errorHandler.captureError(error as Error, {
        component: 'ProductionInfrastructure',
        action: 'initialize',
      });
    }
  }

  private initializePerformanceObserver() {
    if (!('PerformanceObserver' in window)) return;

    // Core Web Vitals monitoring
    try {
      // Largest Contentful Paint (LCP)
      new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const lcp = entry.startTime;
          if (lcp > 2500) {
            errorHandler.addBreadcrumb(
              'Poor LCP detected',
              'performance',
              { lcp, threshold: 2500 }
            );
          }
        }
      }).observe({ entryTypes: ['largest-contentful-paint'] });

      // First Input Delay (FID)
      new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const fid = (entry as any).processingStart - entry.startTime;
          if (fid > 100) {
            errorHandler.addBreadcrumb(
              'Poor FID detected',
              'performance',
              { fid, threshold: 100 }
            );
          }
        }
      }).observe({ entryTypes: ['first-input'] });

      // Cumulative Layout Shift (CLS)
      let cls = 0;
      new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!(entry as any).hadRecentInput) {
            cls += (entry as any).value;
          }
        }
        
        if (cls > 0.1) {
          errorHandler.addBreadcrumb(
            'Poor CLS detected',
            'performance',
            { cls, threshold: 0.1 }
          );
        }
      }).observe({ entryTypes: ['layout-shift'] });
    } catch (error) {
      console.warn('Performance observer setup failed:', error);
    }
  }

  private setupErrorTracking() {
    // Global error handler
    window.addEventListener('error', (event) => {
      errorHandler.captureError(event.error || new Error(event.message), {
        component: 'GlobalErrorHandler',
        action: 'windowError',
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    });

    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      errorHandler.captureError(
        new Error(`Unhandled promise rejection: ${event.reason}`),
        {
          component: 'GlobalErrorHandler',
          action: 'unhandledRejection',
          metadata: { reason: event.reason },
        }
      );
    });
  }

  private scheduleRateLimitCleanup() {
    // Clean up rate limits every hour
    setInterval(async () => {
      try {
        await supabase.rpc('cleanup_rate_limits');
        console.log('Rate limit cleanup completed');
      } catch (error) {
        errorHandler.captureError(error as Error, {
          component: 'ProductionInfrastructure',
          action: 'rateLimitCleanup',
        });
      }
    }, 60 * 60 * 1000); // 1 hour
  }

  // Health check endpoint
  async healthCheck() {
    const checks = {
      database: false,
      edgeFunctions: false,
      storage: false,
      timestamp: new Date().toISOString(),
    };

    try {
      // Test database connection
      const { error: dbError } = await supabase.from('users').select('id').limit(1);
      checks.database = !dbError;

      // Test edge functions
      const { error: funcError } = await supabase.functions.invoke('rate-limiter', {
        body: { action: 'health-check', userId: 'test' },
      });
      checks.edgeFunctions = !funcError;

      // Test storage
      const { error: storageError } = await supabase.storage.listBuckets();
      checks.storage = !storageError;

    } catch (error) {
      errorHandler.captureError(error as Error, {
        component: 'ProductionInfrastructure',
        action: 'healthCheck',
      });
    }

    return checks;
  }

  // Get system metrics
  getMetrics() {
    const memory = (performance as any).memory;
    return {
      memory: memory ? {
        used: Math.round(memory.usedJSHeapSize / 1048576), // MB
        total: Math.round(memory.totalJSHeapSize / 1048576), // MB
        limit: Math.round(memory.jsHeapSizeLimit / 1048576), // MB
      } : null,
      timing: performance.timing ? {
        domReady: performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart,
        loadComplete: performance.timing.loadEventEnd - performance.timing.navigationStart,
      } : null,
      connection: (navigator as any).connection ? {
        effectiveType: (navigator as any).connection.effectiveType,
        downlink: (navigator as any).connection.downlink,
        rtt: (navigator as any).connection.rtt,
      } : null,
    };
  }
}

// Global instance
export const productionInfrastructure = new ProductionInfrastructure();

// Auto-initialize when imported
if (typeof window !== 'undefined') {
  productionInfrastructure.initialize();
}